// components/widgets/widget-card.js
// ═══════════════════════════════════════════════════════════
// WIDGET CARD — global card chrome (Linear + Bloomberg + Things 3 aesthetic)
// ═══════════════════════════════════════════════════════════
// One card component for every widget across Shift. Chrome is
// uniform; body content varies.
//
// Header:
//   ◆  Today's Briefing  ●                          [⋮⋮ edit-only]
//   (glyph + serif title + live status dot     +    drag grip)
//
// Body:
//   - max-height + scrollable
//   - top/bottom fade shadows (auto-hide at edges)
//   - hides each widget's internal .ws-section-header
//
// Footer (always visible):
//   jarvis surface · live                            [↻] [⤢] [✕ edit-only]
//   (category · status text)         (refresh / expand / remove)
// ═══════════════════════════════════════════════════════════

import { createContext, forwardRef, useState, useEffect, useRef, useCallback, useContext } from 'react';
import { getSectionComponent } from '../../features/workstations/dashboard-sections/registry';
import { getWidget, widgetGlyph, CATEGORY_LABELS } from '../../lib/widgets/registry';
import { AiNameContext } from '../../features/workstations/shared/shift-read';
import WidgetExpandModal from './widget-expand-modal';
import WidgetStatusDot from './widget-status-dot';

const DEFAULT_MAX_HEIGHT = 560;
const DEFAULT_MIN_HEIGHT = 140;

// Commit 72 — WidgetFooterContext lets any widget body inject its
// own action button into the card's chrome footer-right slot
// (alongside expand / remove). Used by Today's Read + Today's
// Briefing for their Regenerate buttons so they sit at the bottom-
// right of the card opposite the "category · live" footer-left text.
//
// Usage in a widget body:
//   import { useWidgetFooterAction } from '.../widget-card';
//   const setFooterAction = useWidgetFooterAction();
//   useEffect(() => {
//     setFooterAction(<button ...>↻ Regenerate</button>);
//     return () => setFooterAction(null);
//   }, [setFooterAction, ...deps]);
const WidgetFooterContext = createContext({ setFooterAction: () => {} });

export function useWidgetFooterAction() {
  const ctx = useContext(WidgetFooterContext);
  return ctx.setFooterAction;
}

const WidgetCard = forwardRef(function WidgetCard(
  {
    widgetKey,
    workstation,
    userId,
    userAiName = null,
    editing = false,
    dragAttributes,
    dragListeners,
    isDragging = false,
    onRemove,
    style,
  },
  ref,
) {
  const meta = getWidget(widgetKey);
  const SectionComponent = getSectionComponent(widgetKey);
  const displayName = (meta?.name || widgetKey).toString();
  const tier = meta?.tier || 'D';
  const glyph = widgetGlyph(meta);

  // Category label with per-user ai_name substitution.
  // The 'jarvis-surface' category reads 'Jarvis Surface' in the
  // registry, but we NEVER leak 'Jarvis' to the UI \u2014 swap in the
  // user's chosen ai_name (or a neutral fallback) at render time.
  // Resolution order:
  //   1. workstation.ai_name  (workstation-scoped override)
  //   2. userAiName prop       (user-level default from userRecord)
  //   3. 'AI'                  (neutral fallback when nothing set)
  const resolvedAiName =
    (workstation?.ai_name && String(workstation.ai_name).trim())
    || (userAiName && String(userAiName).trim())
    || null;
  const rawCategoryLabel = CATEGORY_LABELS[meta?.category] || meta?.category || 'Widget';
  const categoryLabel = meta?.category === 'jarvis-surface'
    ? (resolvedAiName ? `${resolvedAiName} Surface` : 'AI Surface')
    : rawCategoryLabel;

  // For V1 every built widget defaults to "live". Future: widgets
  // emit their own status (stale, error, regenerating).
  const status = meta?.status || (meta?.built ? 'live' : 'empty');

  const bodyCfg = meta?.body || {};
  const maxHeight = bodyCfg.maxHeight || DEFAULT_MAX_HEIGHT;
  const minHeight = bodyCfg.minHeight || DEFAULT_MIN_HEIGHT;
  const isExpandable = !!bodyCfg.expandable;

  const [expanded, setExpanded] = useState(false);
  const [scrollState, setScrollState] = useState({ top: true, bottom: true });
  const bodyRef = useRef(null);
  // Commit 72 — slot for widget-injected footer-right action button
  const [footerAction, setFooterActionState] = useState(null);
  const setFooterAction = useCallback((node) => setFooterActionState(node), []);

  // Detect scroll position to toggle fade shadows
  const updateScrollState = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const atTop = el.scrollTop <= 2;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 2;
    setScrollState({ top: atTop, bottom: atBottom });
  }, []);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    for (const child of el.children) ro.observe(child);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  // Header drag attachment — only in edit mode, applied to whole header
  const headerDragProps = editing
    ? { ...dragAttributes, ...dragListeners, 'aria-label': `Drag ${displayName}` }
    : {};

  return (
    <>
      <article
        ref={ref}
        className={`ws-card ws-card-tier-${tier} ${editing ? 'is-editing' : ''} ${isDragging ? 'is-dragging' : ''}`}
        style={style}
        data-widget={widgetKey}
        role="article"
        aria-labelledby={`widget-title-${widgetKey}`}
      >
        {/* ── HEADER ── */}
        <header
          className={`ws-card-header ${editing ? 'is-draggable' : ''}`}
          {...headerDragProps}
        >
          <div className="ws-card-header-left">
            <span
              className="ws-card-glyph"
              data-tier={tier}
              aria-hidden
              title={`Tier ${tier}`}
            >
              {glyph}
            </span>
            <h3
              id={`widget-title-${widgetKey}`}
              className="ws-card-title"
              data-tier={tier}
            >
              {displayName}
            </h3>
            <WidgetStatusDot status={status} label={`${categoryLabel} · ${status}`} />
          </div>
          <div className="ws-card-header-right">
            {editing && (
              <span className="ws-card-drag-grip" aria-hidden title="Drag to move">
                ⋮⋮
              </span>
            )}
          </div>
        </header>

        {/* ── BODY ──
            Scroll contract: max-height + overflow-y:auto go DIRECTLY
            on the body. The wrap is only here to be a positioning
            context for the top/bottom fade overlays. This is the
            dumbest pattern that always works across browsers —
            zero dependence on the flex-parent chain having a
            bounded height. ── */}
        <div
          className={`ws-card-body-wrap ${scrollState.top ? 'is-at-top' : ''} ${scrollState.bottom ? 'is-at-bottom' : ''}`}
        >
          <div
            ref={bodyRef}
            className="ws-card-body"
            style={{ maxHeight: `${maxHeight}px`, minHeight: `${minHeight}px` }}
          >
            {/* Commit 25: AiNameContext provider so ShiftRead
                inside any section automatically uses Tim's
                ai_name (e.g. "Emily's Read") without having to
                drill props through every section component.
                Commit 72: WidgetFooterContext provider so widgets
                can inject footer-right actions (e.g. Regenerate)
                into the card chrome. */}
            <AiNameContext.Provider value={resolvedAiName}>
              <WidgetFooterContext.Provider value={{ setFooterAction }}>
                <SectionComponent
                  workstation={workstation}
                  userId={userId}
                  sectionKey={widgetKey}
                />
              </WidgetFooterContext.Provider>
            </AiNameContext.Provider>
          </div>
          <div className="ws-card-body-fade-top" aria-hidden />
          <div className="ws-card-body-fade-bottom" aria-hidden />
        </div>

        {/* ── FOOTER — always visible (status strip) ── */}
        <footer className="ws-card-footer">
          <div className="ws-card-footer-left">
            <span className="ws-card-footer-meta">
              {categoryLabel.toLowerCase()}{status && status !== 'empty' ? ` · ${status}` : ''}
            </span>
          </div>
          <div className="ws-card-footer-right">
            {/* Commit 72 — widget-injected action (e.g. Regenerate
                from Today's Read / Today's Briefing). Sits to the
                LEFT of the icon buttons (expand / remove) so it's
                visually opposite the "category · live" footer-left
                text per Tim's request. */}
            {footerAction && !editing && (
              <div className="ws-card-footer-action">
                {footerAction}
              </div>
            )}
            {isExpandable && !editing && (
              <button
                type="button"
                className="ws-card-icon-btn"
                onClick={() => setExpanded(true)}
                title="Expand"
                aria-label={`Expand ${displayName}`}
              >
                <span aria-hidden>⤢</span>
              </button>
            )}
            {editing && (
              <button
                type="button"
                className="ws-card-icon-btn ws-card-icon-btn-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRemove) onRemove(widgetKey);
                }}
                title="Remove from dashboard"
                aria-label={`Remove ${displayName} from dashboard`}
              >
                <span aria-hidden>✕</span>
              </button>
            )}
          </div>
        </footer>
      </article>

      {/* ── EXPAND MODAL ── */}
      {isExpandable && (
        <WidgetExpandModal
          open={expanded}
          onClose={() => setExpanded(false)}
          widgetKey={widgetKey}
          title={displayName}
        >
          {/* Commit 25: same AI name context for the expand-modal
              copy of the section, so ShiftRead label is consistent
              between the inline body and the focused-reading modal. */}
          <AiNameContext.Provider value={resolvedAiName}>
            <SectionComponent
              workstation={workstation}
              userId={userId}
              sectionKey={widgetKey}
            />
          </AiNameContext.Provider>
        </WidgetExpandModal>
      )}
    </>
  );
});

export default WidgetCard;
